import { NodeWrapper } from "./NodeWrapper";
import { ContentCopyOutlined as ContentCopy, DeleteOutlined as Delete, FormatListBulletedOutlined as FormatListBulleted } from '@mui/icons-material';
import React, { memo } from "react";
import { Handle } from "react-flow-renderer";

import { Tooltip as CoreTooltip, IconButton as CoreIconButton } from '@material-ui/core';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';


import { useNodeStorage } from "../../../stores/useNodeStorage";
import { mkHeaderStyle, HEADER_ICON_STYLE, HEADER_TEXT_STYLE, mkInputHandleStyle, mkOutputHandleStyle } from "./nodeStyles";
import { i18n } from "../../../translate/i18n";

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: '#1e293b',
    color: '#f8fafc',
    maxWidth: 380,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #334155',
    padding: '12px 14px',
    borderRadius: '8px',
    fontFamily: "'Inter', 'Roboto', sans-serif",
    lineHeight: 1.5,
  },
  arrow: { color: '#1e293b' },
}))(CoreTooltip);

const useStyles = makeStyles((theme) => ({
  pulseIcon: {
    color: '#ffffff',
    borderRadius: '50%',
    animation: '$glowPulse 1.5s infinite',
  },
  '@keyframes glowPulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.9)' },
    '70%': { boxShadow: '0 0 0 8px rgba(255, 255, 255, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)' },
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// Nodo: Lista Interactiva 
// Plan 904 — 15-05-2026
// Cada fila de la lista genera un cable de salida independiente.
// Límite: hasta 10 filas (restricción de la API Oficial de Meta).
// ─────────────────────────────────────────────────────────────────────────────

const META_BLUE = "#1a3a6b";
const META_BLUE_LIGHT = "#e8eef5";
const CABLE_COLOR = "#1a3a6b";
export default memo(({
  data,
  isConnectable,
  id,
  selected
}) => {
  const classes = useStyles();
  const storageItems = useNodeStorage();
  const rows = data.rows || [{
    id: "row_1",
    title: ""
  }];
  return <NodeWrapper id={id} selected={selected} color="#3F51B5">
      {/* ── Floating Toolbar ─────────────────────────────────────────────── */}
      

      {/* ── Handle de entrada (izquierda) ────────────────────────────────── */}
      <Handle type="target" position="left" style={mkInputHandleStyle("#3F51B5")} isConnectable={isConnectable} />

      {/* ── Encabezado ───────────────────────────────────────────────────── */}
      <div style={{ ...mkHeaderStyle("#3F51B5"), justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <FormatListBulleted style={HEADER_ICON_STYLE} />
        <div style={HEADER_TEXT_STYLE}>
          {data.name || i18n.t("flowBuilder.interactiveList.name")}
        </div>
        </div>
        <HtmlTooltip
          arrow
          title={
            <React.Fragment>
              <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#818cf8' }}>
                💡 {i18n.t("flowBuilder.tooltips.list.title")}
              </div>
              <div style={{ marginBottom: '0' }}>
                <div dangerouslySetInnerHTML={{ __html: i18n.t("flowBuilder.tooltips.list.body") }} />
              </div>
            </React.Fragment>
          }
          placement="right"
        >
          <CoreIconButton size="small" style={{ padding: '4px', marginLeft: '2px' }} className={classes.pulseIcon}>
            <HelpOutlineIcon style={{ fontSize: '15px' }} />
          </CoreIconButton>
        </HtmlTooltip>
      </div>

      {/* ── Header del mensaje ───────────────────────────────────────────── */}
      {data.header && <div style={{
      padding: "8px 12px 0 12px"
    }}>
          <div style={{
        fontSize: "11px",
        fontWeight: "600",
        color: META_BLUE,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}>
            {data.header}
          </div>
        </div>}

      {/* ── Cuerpo del mensaje ───────────────────────────────────────────── */}
      <div style={{
      padding: "6px 12px 4px 12px"
    }}>
        <div style={{
        color: "#475569",
        fontSize: "12px",
        lineHeight: "1.5",
        maxHeight: "50px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: "2",
        WebkitBoxOrient: "vertical",
        backgroundColor: "#f8fafc",
        padding: "6px 8px",
        borderRadius: "6px",
        border: "1px solid #f1f5f9"
      }}>
          {data.body || <span style={{
          color: "#94a3b8",
          fontStyle: "italic"
        }}>{i18n.t("flowBuilder.interactiveList.messageBody")}...</span>}
        </div>
      </div>

      {/* ── Botón del menú ───────────────────────────────────────────────── */}
      {data.buttonText && <div style={{
      padding: "2px 12px 6px 12px"
    }}>
          <div style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        backgroundColor: "#f8fafc",
        border: `1px solid ${META_BLUE}40`,
        borderRadius: "20px",
        padding: "3px 10px",
        fontSize: "11px",
        color: META_BLUE,
        fontWeight: "600"
      }}>
            <FormatListBulleted sx={{
          width: "11px",
          height: "11px"
        }} />
            {data.buttonText}
          </div>
        </div>}

      {/* ── Filas de la lista ────────────────────────────────────────────── */}
      <div style={{
      padding: "4px 12px 10px 12px"
    }}>
        <div style={{
        fontSize: "10px",
        fontWeight: "600",
        color: "#64748b",
        marginBottom: "4px",
        textTransform: "uppercase",
        letterSpacing: "0.5px"
      }}>
          {i18n.t("flowBuilder.interactiveList.listOptions")} ({rows.length}/10)
        </div>
        {rows.map((row, idx) => <div key={row.id || idx} style={{
        position: "relative",
        marginBottom: "4px"
      }}>
            <div style={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f8fafc",
          border: `1px solid ${META_BLUE}40`,
          borderRadius: "6px",
          padding: "5px 8px",
          paddingRight: "20px"
        }}>
              <span style={{
            fontSize: "12px",
            color: META_BLUE,
            fontWeight: "600",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}>
                {row.title || <span style={{
              color: "#94a3b8",
              fontStyle: "italic"
            }}>{i18n.t("flowBuilder.interactiveList.optionLabel", {
                index: idx + 1
              }).split("(")[0].trim()}...</span>}
              </span>
              {row.description && <span style={{
            fontSize: "10px",
            color: "#64748b",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}>
                  {row.description}
                </span>}
            </div>
            {/* Cable de salida por cada fila */}
            <Handle type="source" position="right" id={`row-${row.id || idx}`} style={{ ...mkOutputHandleStyle("#3F51B5"), right: "-24px" }} isConnectable={isConnectable} />
          </div>)}
      </div>

      {/* ── Puerto de Respuesta Libre / Fallback ───────────────────────────── */}
      <div style={{
          marginTop: "4px",
          borderTop: "1px dashed #e2e8f0",
          padding: "10px 12px 10px 12px"
      }}>
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <div style={{
                  fontSize: "11px",
                  color: "#64748b",
                  fontWeight: "600",
                  paddingLeft: "26px"
              }}>
                  {i18n.t("flowBuilder.interactiveButtons.fallback", { defaultValue: "Respuesta Libre / Fallback" })}
              </div>
              <Handle
                  type="source"
                  position="right"
                  id="fallback"
                  style={{ ...mkOutputHandleStyle("#ef4444"), right: "-24px" }}
                  isConnectable={isConnectable}
              />
          </div>
      </div>
    </NodeWrapper>;
});
